import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card'
import { Badge } from '@shared/components/ui/badge'
import { Calendar, CreditCard, DollarSign, Clock } from 'lucide-react'

interface PaymentInfoCardProps {
  subscription: {
    billing_cycle: string
    plan_name?: string
    plan_monthly_price_cents?: number
    plan_annual_price_cents?: number
    current_period_end?: string
    status: string
    payment_status?: string
    trial_ends_at?: string
  }
}

export const PaymentInfoCard: React.FC<PaymentInfoCardProps> = ({ subscription }) => {
  const getCurrentPrice = () => {
    const cents = subscription.billing_cycle === 'annual' 
      ? subscription.plan_annual_price_cents 
      : subscription.plan_monthly_price_cents
    return cents ? (cents / 100).toFixed(2) : '—'
  }

  const getBillingFrequency = () => {
    return subscription.billing_cycle === 'annual' ? 'year' : 'month'
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getNextPaymentDate = () => {
    if (subscription.status === 'canceled') return 'No future payments'
    if (subscription.status === 'cancel_at_period_end') return 'Final payment processed'
    if (subscription.trial_ends_at && new Date(subscription.trial_ends_at) > new Date()) {
      return formatDate(subscription.trial_ends_at)
    }
    return formatDate(subscription.current_period_end)
  }

  const isActive = subscription.status === 'active' || subscription.status === 'trialing'

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <DollarSign className="h-5 w-5" />
          Payment Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="text-sm font-medium text-muted-foreground">Current Plan</div>
            <div className="text-lg font-semibold">{subscription.plan_name || '—'}</div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-muted-foreground">Billing Cycle</div>
            <div className="text-lg font-semibold capitalize">{subscription.billing_cycle || '—'}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="text-sm font-medium text-muted-foreground">Current Price</div>
            <div className="text-2xl font-bold text-green-600">
              ${getCurrentPrice()}
              <span className="text-sm font-normal text-muted-foreground ml-1">
                / {getBillingFrequency()}
              </span>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-muted-foreground">Payment Status</div>
            <Badge 
              variant={subscription.payment_status === 'active' ? 'default' : 'destructive'}
              className="text-sm"
            >
              {subscription.payment_status === 'active' ? '✅ Active' : 
               subscription.payment_status === 'past_due' ? '⚠️ Past Due' :
               subscription.payment_status === 'canceled' ? '❌ Canceled' :
               subscription.payment_status === 'cancel_at_period_end' ? '⏰ Canceling' :
               '❌ Failed'}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Next Payment
            </div>
            <div className="text-lg font-semibold">{getNextPaymentDate()}</div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Period Ends
            </div>
            <div className="text-lg font-semibold">{formatDate(subscription.current_period_end)}</div>
          </div>
        </div>

        {subscription.trial_ends_at && new Date(subscription.trial_ends_at) > new Date() && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-blue-800">
              <span className="text-lg">🆓</span>
              <div>
                <div className="font-medium">Trial Period</div>
                <div className="text-sm">Trial ends on {formatDate(subscription.trial_ends_at)}</div>
              </div>
            </div>
          </div>
        )}

        <div className="pt-2 border-t">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-muted-foreground">Subscription Status</div>
            <div className={`text-sm font-medium ${isActive ? 'text-green-600' : 'text-red-600'}`}>
              {isActive ? '🟢 Active' : '🔴 Inactive'}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

